/**
 * Brand color scales - consistent across all themes
 * These are the design system's color palette that doesn't change between light/dark modes
 */
export const black = {
  100: '#999999',
  200: '#8F8F8F',
  300: '#858585',
  400: '#7A7A7A',
  500: '#707070',
  600: '#666666',
  700: '#5C5C5C',
  800: '#525252',
  900: '#474747',
  1000: '#3D3D3D',
  1100: '#333333',
  1200: '#222222',
  1300: '#141414',
  1400: '#0A0A0A',
} as const;

export const grey = {
  100: '#FFFFFF',
  200: '#F5F5F5',
  300: '#EBEBEB',
  400: '#E0E0E0',
  500: '#D6D6D6',
  600: '#CCCCCC',
  700: '#C2C2C2',
  800: '#B8B8B8',
  900: '#ADADAD',
  1000: '#A3A3A3',
} as const;

export const red = {
  100: '#FFE9E7',
  200: '#FAC8C3',
  300: '#F89F9A',
  400: '#F37370',
  500: '#E84749',
  600: '#D32029',
  700: '#A61D24',
  800: '#791A1F',
  900: '#58181C',
  1000: '#431418',
  1100: '#2A1215',
} as const;

export const orange = {
  100: '#FFF6F1',
  200: '#FAD4BC',
  300: '#F8B692',
  400: '#F3956A',
  500: '#E87040',
  600: '#D84A1B',
  700: '#AA3E19',
  800: '#7C3118',
  900: '#592716',
  1000: '#441D12',
  1100: '#2B1611',
} as const;

export const yellow = {
  100: '#FFFDF5',
  200: '#FFFBE6',
  300: '#FFF1B8',
  400: '#FFE58F',
  500: '#FFD666',
  600: '#FFC53D',
  700: '#FAAD14',
  800: '#D48806',
  900: '#AD6800',
  1000: '#874D00',
  1100: '#613400',
} as const;

export const green = {
  100: '#F0FDF6',
  200: '#DDFBEB',
  300: '#BDF5D8',
  400: '#4FD993',
  500: '#4FD993',
  600: '#32D583',
  700: '#1B9E5C',
  800: '#187D4B',
  900: '#19623E',
  1000: '#165135',
  1100: '#062D1B',
} as const;

export const vividGreen = {
  100: '#FBFFE1',
  200: '#F0FAB5',
  300: '#E4F88B',
  400: '#C9E75D',
  500: '#A9D134',
  600: '#8BBB11',
  700: '#6F9412',
  800: '#536D13',
  900: '#3E4F13',
  1000: '#2E3C10',
  1100: '#1F2611',
} as const;

export const lime = {
  100: '#FFFFF3',
  200: '#FEFFE6',
  300: '#FFFFB8',
  400: '#FFFB8F',
  500: '#FFF566',
  600: '#FFEC3D',
  700: '#FADB14',
  800: '#D4B106',
  900: '#AD8B00',
  1000: '#876800',
  1100: '#614700',
} as const;

export const blue = {
  100: '#EFF8FF',
  200: '#DAEEFF',
  300: '#BEE2FF',
  400: '#91D1FF',
  500: '#53B1FD',
  600: '#3795FA',
  700: '#2178EF',
  800: '#1960DC',
  900: '#1B4EB2',
  1000: '#1C458C',
  1100: '#162B55',
} as const;

export const violet = {
  100: '#F5F0FF',
  200: '#EDE4FF',
  300: '#DDCDFF',
  400: '#C5A5FF',
  500: '#AB72FF',
  600: '#953AFF',
  700: '#8D12FF',
  800: '#8101FF',
  900: '#6800CE',
  1000: '#5A02B0',
  1100: '#360078',
} as const;

export const ocean = {
  100: '#F4F3FE',
  200: '#EBE9FC',
  300: '#D8D5FC',
  400: '#BAB4F9',
  500: '#978BF4',
  600: '#755BEF',
  700: '#623AE6',
  800: '#5329D3',
  900: '#4521B1',
  1000: '#3A1D91',
  1100: '#221062',
} as const;

export const purple = {
  100: '#FAF5FD',
  200: '#F4EAFC',
  300: '#E8D4F8',
  400: '#D9B3F1',
  500: '#C485EA',
  600: '#A244D8',
  700: '#923AC0',
  800: '#7A2D9F',
  900: '#652782',
  1000: '#56246B',
  1100: '#350D46',
} as const;

export const pink = {
  100: '#FCF1FA',
  200: '#FBE5F9',
  300: '#F7CBF4',
  400: '#F1A2EA',
  500: '#ED6CD9',
  600: '#EB48C8',
  700: '#DF34AA',
  800: '#C42A8D',
  900: '#A02272',
  1000: '#851D60',
  1100: '#520B38',
} as const;

/**
 * All extended color scales combined for easy palette spreading
 */
export const colorScales = {
  black,
  grey,
  red,
  orange,
  yellow,
  green,
  vividGreen,
  lime,
  blue,
  violet,
  ocean,
  purple,
  pink,
} as const;
